import { generatePath } from '@toggle/helpers';
import { formatTicker } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useEntities } from '~/hooks/use-entities';
import { useCompanyCompetitorWithThemes } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { useCompetitorsDrawer } from '~/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/useCompetitorsDrawer';
import { TopRankIcon } from '~/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import * as S from './CompetitorsKnowledgeGraph.styles';

export interface CompetitorsKnowledgeGraphProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const CompetitorsKnowledgeGraph = ({
  entityTag,
  isInDrawer = true,
}: CompetitorsKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { openDrawer } = useCompetitorsDrawer();

  const {
    data: competitorsData = [],
    isFetching,
    isError,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit: 3,
  });

  const entityTags = competitorsData.map(item => item.company);

  const { data: entitiesData = [], isLoading: isEntitiesLoading } = useEntities(
    {
      entityTags,
      enabled: !!entityTags.length,
    }
  );

  if (isError) {
    throw new Error('Failed to load CompetitorsKnowledgeGraph');
  }

  // Create a map of entity tag to competitor data (for rank lookup)
  const competitorDataByTag = new Map(
    competitorsData.map(item => [item.company, item])
  );

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["competitors"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.Competitors,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const onClickHandler = isInDrawer
    ? (competitorEntityTag: string) => {
        const requestPath = generatePath({
          basePath: appPaths.assetOverview,
          params: {
            [queryKeys.assetOverviewAsset]: competitorEntityTag,
          },
        });
        navigate(requestPath);
      }
    : openDrawer;

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.knowledgeGraph.subtab.competitors')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('competitors')}
      />
      {isFetching || isEntitiesLoading ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <S.CompetitorRow key={idx}>
              <LoadingCell />
            </S.CompetitorRow>
          ))}
        </div>
      ) : (
        <div>
          {entitiesData.map(entity => {
            const competitorData = competitorDataByTag.get(entity.tag);
            const rank = competitorData?.relationship.rank;

            if (!rank) {
              return null;
            }

            return (
              <S.CompetitorRow
                onClick={() => onClickHandler(entity.tag)}
                key={entity.tag}
              >
                <S.CompetitorNameWrapper>
                  <AssetLogoContainer entity={entity} size={32} />
                  <S.FlexColumn>
                    <S.TitleLabel>
                      {entity.name_short || entity.name}
                    </S.TitleLabel>
                    <S.CompetitorName>{formatTicker(entity)}</S.CompetitorName>
                  </S.FlexColumn>
                </S.CompetitorNameWrapper>
                <TopRankIcon rank={rank} iconSize={28} />
              </S.CompetitorRow>
            );
          })}
        </div>
      )}
    </div>
  );
};

CompetitorsKnowledgeGraph.Styled = S;
